import { isElementBlacklisted } from 'model/dom/is-element-blacklisted';
import type { KeysService } from 'plugins/keys/service/config/types';
import { clearStateKeys } from 'plugins/keys/service/config/types';
import { convertCodeToKey } from 'plugins/keys/service/model/convert-code-to-key';

/**
 * The keys service tracks keys being pressed and calls provided callbacks for onKeyDown and onKeyUp.
 */
export function createKeysService({
  onKeydown,
  onKeyup,
}: {
  onKeydown?: Function;
  onKeyup?: Function;
} = {}): KeysService.API {
  let enabled = true;
  const keysPressed: Set<string> = new Set();

  document.addEventListener('keydown', onKeydownHandler);
  document.addEventListener('keyup', onKeyupHandler);
  window.addEventListener('blur', clearPressedKeys);
  window.addEventListener('contextmenu', clearPressedKeys);

  return {
    isPressed,
    getPressedKeys,
    disable,
    enable,
    destroy,
  };

  /**
   * Returns true if a key is currently pressed down (case insensitive)
   */
  function isPressed(key: string): boolean {
    return keysPressed.has(key.toLowerCase());
  }

  /**
   * Returns a list of keys being pressed.
   */
  function getPressedKeys(): string[] {
    return [...keysPressed].sort();
  }

  /**
   * Keydown event handler.
   */
  function onKeydownHandler(event: KeyboardEvent): void {
    const activeElement = <HTMLElement | null>document.activeElement;

    if (!enabled || isElementBlacklisted(activeElement)) {
      return;
    }

    const key = convertCodeToKey(event.code);

    if (key) {
      keysPressed.add(key);
      onKeydown?.(key, event);
    }
  }

  /**
   * Keyup event handler.
   */
  function onKeyupHandler(event: KeyboardEvent): void {
    const key = convertCodeToKey(event.code);

    if (key) {
      // NOTE: this serves for clearing sticky keys (e.g. 'cmd' + 'c' would make 'c' stick)
      if (clearStateKeys.includes(key)) {
        keysPressed.clear();
      }

      keysPressed.delete(key);
      onKeyup?.(key, event);
    }
  }

  /**
   * Clear pressed keys.
   */
  function clearPressedKeys(): void {
    keysPressed.clear();
  }

  /**
   * Stop registering keys
   */
  function disable(): void {
    enabled = false;

    keysPressed.clear();
  }

  /**
   * Start registering keys
   */
  function enable(): void {
    enabled = true;
  }

  /**
   * Remove listeners
   */
  function destroy(): void {
    document.removeEventListener('keydown', onKeydownHandler);
    document.removeEventListener('keyup', onKeyupHandler);
    window.removeEventListener('blur', clearPressedKeys);
    window.removeEventListener('contextmenu', clearPressedKeys);

    keysPressed.clear();
  }
}
